import React, { useState } from 'react';
import Modal from '../../components/ui/Modal';
import { Button, Badge, Input } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { api } from '../../lib/api';
import { roleOptionLabel } from '../../lib/roleOptions';

export default function DeleteUserModal({ user, currentUser, onClose, onDeleted }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  if (!user) return null;

  const emailMatches = confirmEmail === user.email;
  const transferTarget = user.marketing_director_id
    ? 'their Marketing Director'
    : 'you';

  const handleDelete = async () => {
    if (!emailMatches || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await api.users.deleteWithBackup({
        targetUser: user,
        deletedByUser: currentUser,
        agencyId: currentUser.agency_id,
      });
      onDeleted(result);
    } catch (err) {
      setError(err?.message || 'Failed to delete user. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={deleting ? () => {} : onClose}
      title={<span className="text-red-600">Permanently Delete User</span>}
      className="max-w-md"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
            {user.full_name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium text-gray-900">{user.full_name}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
            <Badge variant="default" className="mt-1">{roleOptionLabel(user.role)}</Badge>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">
          This cannot be undone. All leads owned by{' '}
          <span className="font-medium text-gray-800">{user.full_name}</span>{' '}
          will be transferred to {transferTarget}. A full backup of their account will be saved
          before deletion.
        </p>

        <Input
          label={`Type "${user.email}" to confirm`}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          disabled={deleting}
          autoComplete="off"
        />

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            disabled={!emailMatches || deleting}
          >
            {deleting ? (
              <>
                <Spinner className="mr-2" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
